import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Typography,
  IconButton,
  Divider
} from '@mui/material';
import {
  Close,
  Edit,
  Delete,
  Person,
  Email,
  AttachMoney,
  Schedule,
  LocationOn
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { CalendarEvent, Listing, EventFormData } from '../../schemas/calendar';

interface EventDialogProps {
  open: boolean;
  onClose: () => void;
  event?: CalendarEvent;
  listings: Listing[];
  isEditing: boolean;
  onSave: (eventData: EventFormData) => void;
  onDelete?: (eventId: string) => void;
  onEdit: () => void;
  selectedSlot?: { start: Date; end: Date; listingId?: string };
}

const EventDialog: React.FC<EventDialogProps> = ({
  open,
  onClose,
  event,
  listings,
  isEditing,
  onSave,
  onDelete,
  onEdit,
  selectedSlot
}) => {
  const [formData, setFormData] = useState<EventFormData>({
    title: '',
    description: '',
    startTime: new Date(),
    endTime: new Date(),
    listingId: '',
    customerName: '',
    customerEmail: '',
    status: 'confirmed',
    price: 0
  });

  useEffect(() => {
    if (event) {
      setFormData({
        title: event.title,
        description: event.description || '',
        startTime: event.startTime,
        endTime: event.endTime,
        listingId: event.listingId,
        customerName: event.customerName || '',
        customerEmail: event.customerEmail || '',
        status: event.status,
        price: event.price || 0
      });
    } else if (selectedSlot) {
      setFormData((prev: any) => ({
        ...prev,
        startTime: selectedSlot.start,
        endTime: selectedSlot.end,
        listingId: selectedSlot.listingId || listings[0]?.id || ''
      }));
    }
  }, [event, selectedSlot, listings]);

  const handleSave = () => {
    onSave(formData);
    onClose();
  };

  const handleDelete = () => {
    if (event && onDelete) {
      onDelete(event.id);
      onClose();
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed': return 'success';
      case 'pending': return 'warning';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const selectedListing = listings.find(l => l.id === formData.listingId);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 1 },
            m: { xs: 1, sm: 2 },
            width: { xs: "calc(100% - 16px)", sm: "100%" },
            maxHeight: { xs: "calc(100dvh - 16px)", sm: "90vh" },
            display: "flex",
            flexDirection: "column",
            boxShadow: 6,
            backgroundColor: "background.paper",
          },
        }}
      >
        <DialogTitle
          sx={{
            background: (theme) => theme.palette.primary.main,
            color: (theme) => theme.palette.primary.contrastText,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: { xs: 1.5, md: 2 },
            px: { xs: 2, md: 3 },
          }}
        >
          <Typography
            variant="h5"
            fontWeight={600}
            sx={{ fontSize: { xs: "1.15rem", md: "1.5rem" }, pr: 1 }}
          >
            {isEditing ? (event ? 'Edit Event' : 'Create Event') : 'Event Details'}
          </Typography>
          <Box>
            {/* {!isEditing && event && (
              <IconButton onClick={onEdit} sx={{ color: 'inherit', mr: 1 }}>
                <Edit />
              </IconButton>
            )} */}
            <IconButton onClick={onClose} sx={{ color: 'inherit' }}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: { xs: 2, md: 3 }, overflowY: "auto" }}>
          {!isEditing && event ? (
            // View Mode
            <Box>
              <Box>
                <Typography
                  sx={{ mt: 1, pr: 1, wordBreak: "break-word" }}
                  variant="h5"
                  fontWeight={600}
                  gutterBottom
                >
                  {event.title}
                </Typography>
                <Chip 
                  label={event.status.toUpperCase()} 
                  color={getStatusColor(event.status)}
                  size="small"
                  sx={{ mb: 2, maxWidth: "100%" }}
                />
              </Box>

              {event.description && (
                <Box>
                  <Typography variant="subtitle2" color="black">Description</Typography>
                  <Typography variant="body1" color="textSecondary" paragraph>
                    {event.description}
                  </Typography>
                </Box>
              )}

              <Divider sx={{ my: 1 }} />

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
                  gap: { xs: 2, sm: 1 },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "flex-start", minWidth: 0 }}>
                  <Schedule sx={{ mr: 1, mt: 0.25, color: "primary.main", flexShrink: 0 }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Start Time
                    </Typography>
                    <Typography variant="body1" sx={{ wordBreak: "break-word" }}>
                      {dayjs(event.startTime).format("MMM DD, YYYY h:mm A")}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "flex-start", minWidth: 0 }}>
                  <Schedule sx={{ mr: 1, mt: 0.25, color: "primary.main", flexShrink: 0 }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2" color="textSecondary">
                      End Time
                    </Typography>
                    <Typography variant="body1" sx={{ wordBreak: "break-word" }}>
                      {dayjs(event.endTime).format("MMM DD, YYYY h:mm A")}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "flex-start", minWidth: 0 }}>
                  <LocationOn sx={{ mr: 1, mt: 0.25, color: "primary.main", flexShrink: 0 }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Listing
                    </Typography>
                    <Typography variant="body1" sx={{ wordBreak: "break-word" }}>
                      {selectedListing?.name}
                    </Typography>
                  </Box>
                </Box>

                {event.price && (
                  <Box sx={{ display: "flex", alignItems: "flex-start", minWidth: 0 }}>
                    <AttachMoney sx={{ mr: 1, mt: 0.25, color: "primary.main", flexShrink: 0 }} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Price
                      </Typography>
                      <Typography variant="body1" sx={{ wordBreak: "break-word" }}>
                        ${event.price}/hr
                      </Typography>
                    </Box>
                  </Box>
                )}

                {event.customerName && (
                  <Box sx={{ display: "flex", alignItems: "flex-start", minWidth: 0 }}>
                    <Person sx={{ mr: 1, mt: 0.25, color: "primary.main", flexShrink: 0 }} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Customer
                      </Typography>
                      <Typography variant="body1" sx={{ wordBreak: "break-word" }}>
                        {event.customerName}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {event.customerId && (
                  <Box sx={{ display: "flex", alignItems: "flex-start", minWidth: 0 }}>
                    <Email sx={{ mr: 1, mt: 0.25, color: "primary.main", flexShrink: 0 }} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Email
                      </Typography>
                      <Typography variant="body1" sx={{ wordBreak: "break-word" }}>
                        {event.customerId}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          ) : (
            // Edit/Create Mode
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 , mt: 2}}>
              <TextField
                fullWidth
                label="Event Title"
                value={formData.title}
                onChange={(e) => setFormData((prev: any) => ({ ...prev, title: e.target.value }))}
              />

              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData((prev: any) => ({ ...prev, description: e.target.value }))}
              />

              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" }, gap: 2 }}>
                <DateTimePicker
                  label="Start Time"
                  value={dayjs(formData.startTime)}
                  onChange={(newValue) => newValue && setFormData((prev: any) => ({ 
                    ...prev, 
                    startTime: newValue.toDate() 
                  }))}
                  sx={{ width: '100%' }}
                />

                <DateTimePicker
                  label="End Time"
                  value={dayjs(formData.endTime)}
                  onChange={(newValue) => newValue && setFormData((prev: any) => ({ 
                    ...prev, 
                    endTime: newValue.toDate() 
                  }))}
                  sx={{ width: '100%' }}
                />

                <FormControl fullWidth required>
                  <InputLabel>Listing</InputLabel>
                  <Select
                    value={formData.listingId}
                    disabled
                    onChange={(e) => setFormData((prev: any) => ({ ...prev, listingId: e.target.value }))}
                    label="Listing"
                  >
                    {listings.map((listing) => (
                      <MenuItem key={listing.id} value={listing.id}>
                        {listing.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    onChange={(e) => setFormData((prev: any) => ({ 
                      ...prev, 
                      status: e.target.value as 'confirmed' | 'pending' | 'cancelled' 
                    }))}
                    label="Status"
                  >
                    <MenuItem value="confirmed">Confirmed</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                  </Select>
                </FormControl> */}

                <TextField
                  fullWidth
                  label="Customer Name"
                  value={formData.customerName}
                  onChange={(e) => setFormData((prev: any) => ({ ...prev, customerName: e.target.value }))}
                />

                <TextField
                  fullWidth
                  label="Customer Email"
                  type="email"
                  value={formData.customerEmail}
                  onChange={(e) => setFormData((prev: any) => ({ ...prev, customerEmail: e.target.value }))}
                />
              </Box>

              <TextField
                fullWidth
                label="Price per Hour"
                type="number"
                value={formData.price}
                onChange={(e) => setFormData((prev: any)  => ({ ...prev, price: Number(e.target.value) }))}
                InputProps={{
                  startAdornment: <AttachMoney sx={{ mr: 1 }} />
                }}
              />
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: { xs: 2, md: 3 }, gap: 1, flexShrink: 0 }}>
          {/* {!isEditing && event && onDelete && (
            <Button
              onClick={handleDelete}
              color="error"
              startIcon={<Delete />}
              sx={{ mr: 'auto' }}
            >
              Delete
            </Button>
          )} */}
          
          <Button onClick={onClose} color="inherit">
            Cancel
          </Button>
          
          {isEditing && (
            <Button
              onClick={handleSave}
              variant="contained"
              sx={{
                background: (theme) => theme.palette.primary.main,
                color: (theme) => theme.palette.primary.contrastText,
                px: 3,
                '&:hover': {
                  background: (theme) => theme.palette.primary.dark,
                },
              }}
            >
              {event ? 'Update' : 'Create'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default EventDialog;